import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, ArrowRight, Zap } from "lucide-react";
import LeagueSwitcher from "@/components/LeagueSwitcher";
import { fetchMatches, generatePrediction, getPrediction } from "@/lib/api";
import { toast } from "sonner";
import { useI18n } from "@/i18n/I18nContext";

function PredictionCard({ match, prediction, onGenerate, loading }) {
  const { t, lang } = useI18n();
  return (
    <div data-testid={`prediction-card-${match.id}`} className="border border-white/10 bg-[#121212] p-6 card-lift">
      <div className="flex items-center justify-between mb-4">
        <span className="text-xs uppercase tracking-[0.2em] text-white/40">{match.league}</span>
        <span className="text-xs font-mono text-white/50">
          {new Date(match.kickoff).toLocaleString(lang, {
            day: "2-digit",
            month: "short",
            hour: "2-digit",
            minute: "2-digit",
          })}
        </span>
      </div>

      <div className="grid grid-cols-3 items-center gap-2 mb-5">
        <div className="text-center">
          <div className="w-12 h-12 bg-white/5 border border-white/10 flex items-center justify-center overflow-hidden mx-auto mb-2">
            {match.home_team.logo_url ? (
              <img src={match.home_team.logo_url} alt={match.home_team.short} className="w-9 h-9 object-contain" />
            ) : (
              <span className="font-mono text-xs">{match.home_team.logo}</span>
            )}
          </div>
          <div className="text-xs uppercase tracking-tight">{match.home_team.short}</div>
        </div>
        <div className="text-center font-display text-3xl text-white/30">{t("match.vs")}</div>
        <div className="text-center">
          <div className="w-12 h-12 bg-white/5 border border-white/10 flex items-center justify-center overflow-hidden mx-auto mb-2">
            {match.away_team.logo_url ? (
              <img src={match.away_team.logo_url} alt={match.away_team.short} className="w-9 h-9 object-contain" />
            ) : (
              <span className="font-mono text-xs">{match.away_team.logo}</span>
            )}
          </div>
          <div className="text-xs uppercase tracking-tight">{match.away_team.short}</div>
        </div>
      </div>

      {prediction ? (
        <div className="space-y-3">
          <div className="border border-volt/30 bg-volt/5 p-3 text-center">
            <div className="text-[10px] uppercase tracking-[0.2em] text-volt/80">{t("pred.predicted")}</div>
            <div className="font-display text-3xl tabular-nums text-volt">{prediction.predicted_score}</div>
          </div>
          <div className="grid grid-cols-3 gap-1 text-center text-xs">
            <div className="bg-white/[0.03] py-2">
              <div className="font-display text-lg tabular-nums">{prediction.home_win_pct}%</div>
              <div className="text-[9px] uppercase text-white/40 tracking-[0.15em]">{t("pred.home")}</div>
            </div>
            <div className="bg-white/[0.03] py-2">
              <div className="font-display text-lg tabular-nums">{prediction.draw_pct}%</div>
              <div className="text-[9px] uppercase text-white/40 tracking-[0.15em]">{t("pred.draw")}</div>
            </div>
            <div className="bg-white/[0.03] py-2">
              <div className="font-display text-lg tabular-nums">{prediction.away_win_pct}%</div>
              <div className="text-[9px] uppercase text-white/40 tracking-[0.15em]">{t("pred.away")}</div>
            </div>
          </div>
          <div className="text-xs text-white/70 leading-relaxed line-clamp-3 italic">&ldquo;{prediction.headline}&rdquo;</div>
          <Link to={`/matches/${match.id}`} data-testid={`prediction-detail-${match.id}`}
            className="inline-flex items-center gap-1 text-xs uppercase tracking-[0.2em] text-volt hover:underline">
            {t("pred.full_analysis")} <ArrowRight className="w-3 h-3" />
          </Link>
        </div>
      ) : (
        <button
          onClick={() => onGenerate(match.id)}
          disabled={loading}
          data-testid={`generate-${match.id}`}
          className="w-full inline-flex items-center justify-center gap-2 bg-volt text-black font-bold uppercase tracking-[0.18em] px-4 py-2.5 text-xs hover:bg-[#b4e500] transition-colors disabled:opacity-60 disabled:cursor-wait"
        >
          {loading ? t("pred.generating") : (<><Zap className="w-4 h-4" /> {t("pred.generate")}</>)}
        </button>
      )}
    </div>
  );
}

export default function Predictions() {
  const { t } = useI18n();
  const [league, setLeague] = useState("all");
  const [matches, setMatches] = useState([]);
  const [predictions, setPredictions] = useState({});
  const [loadingId, setLoadingId] = useState(null);

  useEffect(() => {
    const params = { status: "upcoming" };
    if (league !== "all") params.league = league;
    fetchMatches(params).then(async (ms) => {
      setMatches(ms);
      const map = {};
      await Promise.all(
        ms.map(async (m) => {
          try {
            const p = await getPrediction(m.id);
            map[m.id] = p;
          } catch (e) { /* none */ }
        })
      );
      setPredictions(map);
    });
  }, [league]);

  const handleGenerate = async (id) => {
    setLoadingId(id);
    try {
      const p = await generatePrediction(id);
      setPredictions((prev) => ({ ...prev, [id]: p }));
      toast.success(t("pred.success"));
    } catch (e) {
      toast.error(t("pred.error"));
    } finally {
      setLoadingId(null);
    }
  };

  return (
    <div data-testid="predictions-page" className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="border-b border-white/10 pb-6">
        <div className="flex items-center gap-3">
          <Sparkles className="w-7 h-7 text-volt" />
          <h1 className="font-display text-5xl sm:text-6xl uppercase tracking-tighter">{t("pred.title")}</h1>
        </div>
        <p className="text-sm text-white/50 mt-2 max-w-2xl">{t("pred.subtitle")}</p>
      </div>

      <div className="mt-6">
        <LeagueSwitcher value={league} onChange={setLeague} testIdPrefix="pred-league" />
      </div>

      <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {matches.length === 0 ? (
          <div className="col-span-full text-center text-white/40 py-20 text-sm uppercase tracking-[0.2em]">
            {t("pred.no_matches")}
          </div>
        ) : (
          matches.map((m) => (
            <PredictionCard
              key={m.id}
              match={m}
              prediction={predictions[m.id]}
              onGenerate={handleGenerate}
              loading={loadingId === m.id}
            />
          ))
        )}
      </div>
    </div>
  );
}
